import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useCurrentUser } from "@/lib/auth";
import { canWriteStammdaten } from "@/lib/permissions";
import { usePageHeader } from "@/layout/usePageHeader";
import { Button } from "@/components/ui/Button";
import { OnlineBookingIntroPanel } from "@/pages/stammdaten/OnlineBookingIntroPanel";
import { OnlineBookingReadinessAlerts } from "@/pages/stammdaten/online-booking/OnlineBookingReadinessAlerts";
import { OnlineBookingSectionNav } from "@/pages/stammdaten/OnlineBookingSectionNav";
import { OnlineBookingAngebotPanel } from "@/pages/stammdaten/online-booking/OnlineBookingAngebotPanel";
import { OnlineBookingBetriebPanel } from "@/pages/stammdaten/online-booking/OnlineBookingBetriebPanel";
import { OnlineBookingRegelnPanel } from "@/pages/stammdaten/online-booking/OnlineBookingRegelnPanel";
import { OnlineBookingSectionIntro } from "@/pages/stammdaten/online-booking/OnlineBookingSectionIntro";
import { OnlineBookingStreckePanel } from "@/pages/stammdaten/online-booking/OnlineBookingStreckePanel";
import { OnlineBookingSubSectionNav } from "@/pages/stammdaten/online-booking/OnlineBookingSubSectionNav";
import {
  parseOnlineBookingSubSection,
  subSectionsForTab,
  type OnlineBookingAngebotSub,
  type OnlineBookingRegelnSub,
  type OnlineBookingStreckeSub,
  type OnlineBookingSubSection,
} from "@/pages/stammdaten/online-booking/onlineBookingSubSections";
import { useOnlineBookingSettings } from "@/pages/stammdaten/online-booking/useOnlineBookingSettings";
import {
  ONLINE_BOOKING_SECTIONS,
  parseOnlineBookingTab,
  type OnlineBookingTab,
} from "@/pages/stammdaten/onlineBookingSections";
import { StammdatenWriteHint } from "@/pages/stammdaten/StammdatenWriteHint";
import type { ArrivalTypeRecord } from "@/types/camping";

export function OnlineBookingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseOnlineBookingTab(searchParams.get("tab"));
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canWrite = canWriteStammdaten(groups);

  const settings = useOnlineBookingSettings(canWrite);

  const [arrivalTypes, setArrivalTypes] = useState<ArrivalTypeRecord[]>([]);
  const [arrivalTypesLoading, setArrivalTypesLoading] = useState(true);

  const subSections = subSectionsForTab(tab);
  const subSection = subSections
    ? parseOnlineBookingSubSection(tab, searchParams.get("section"))
    : null;

  const regelnSubSection: OnlineBookingRegelnSub | null =
    tab === "regeln" ? (subSection as OnlineBookingRegelnSub) : null;
  const streckeSubSection: OnlineBookingStreckeSub | null =
    tab === "strecke" ? (subSection as OnlineBookingStreckeSub) : null;
  const angebotSubSection: OnlineBookingAngebotSub | null =
    tab === "angebot" ? (subSection as OnlineBookingAngebotSub) : null;

  const loadArrivalTypes = useCallback(() => {
    setArrivalTypesLoading(true);
    api
      .get<ArrivalTypeRecord[]>("/api/v1/arrival-types?include_inactive=true")
      .then(setArrivalTypes)
      .catch((e: Error) => settings.setError(e.message))
      .finally(() => setArrivalTypesLoading(false));
  }, [settings.setError]);

  const activeSection = useMemo(
    () => ONLINE_BOOKING_SECTIONS.find((s) => s.id === tab) ?? ONLINE_BOOKING_SECTIONS[0]!,
    [tab],
  );

  useEffect(() => {
    setPageMeta({
      title: "Stammdaten",
      subtitle: tab === "overview" ? "Online-Buchung" : `Online-Buchung · ${activeSection.shortLabel}`,
    });
    settings.load();
    loadArrivalTypes();
    return () => setPageMeta(null);
  }, [activeSection.shortLabel, loadArrivalTypes, setPageMeta, settings.load, tab]);

  function onTab(next: OnlineBookingTab) {
    setSearchParams(next === "overview" ? {} : { tab: next });
  }

  function onSubSection(next: OnlineBookingSubSection) {
    const params: Record<string, string> = { tab };
    const defaultSub = subSections?.[0]?.id;
    if (next !== defaultSub) {
      params.section = next;
    }
    setSearchParams(params);
  }

  return (
    <div className="space-y-6">
      <OnlineBookingReadinessAlerts
        placement="settings"
        reloadToken={settings.success ? 1 : 0}
      />

      <OnlineBookingSectionNav tab={tab} onTab={onTab} />

      {!canWrite ? <StammdatenWriteHint /> : null}
      {settings.error ? <p className="text-sm text-status-red">{settings.error}</p> : null}

      {tab === "overview" ? <OnlineBookingIntroPanel /> : null}

      {tab !== "overview" && settings.loading ? (
        <p className="text-sm text-label-secondary">Einstellungen werden geladen…</p>
      ) : tab !== "overview" ? (
        <form onSubmit={(e) => void settings.onSave(e)} className="space-y-6">
          {subSections && subSection ? (
            <OnlineBookingSubSectionNav
              sections={subSections}
              activeSection={subSection}
              onSection={onSubSection}
            />
          ) : null}

          <OnlineBookingSectionIntro tab={tab} subSection={subSection} />

          {tab === "betrieb" ? (
            <OnlineBookingBetriebPanel
              canWrite={canWrite}
              publicBookingEnabled={settings.publicBookingEnabled}
              setPublicBookingEnabled={settings.setPublicBookingEnabled}
              onlineBookingMode={settings.onlineBookingMode}
              setOnlineBookingMode={settings.setOnlineBookingMode}
              onlineBookingAvailabilityCheck={settings.onlineBookingAvailabilityCheck}
              setOnlineBookingAvailabilityCheck={settings.setOnlineBookingAvailabilityCheck}
              touristIntegrationEnabled={settings.touristIntegrationEnabled}
              autoSendConfirmationEmail={settings.autoSendConfirmationEmail}
              setAutoSendConfirmationEmail={settings.setAutoSendConfirmationEmail}
            />
          ) : null}

          {tab === "regeln" && regelnSubSection ? (
            <OnlineBookingRegelnPanel
              canWrite={canWrite}
              subSection={regelnSubSection}
              seasons={settings.seasons}
              setSeasons={settings.setSeasons}
              minStay={settings.minStay}
              setMinStay={settings.setMinStay}
              sameDayCutoff={settings.sameDayCutoff}
              setSameDayCutoff={settings.setSameDayCutoff}
            />
          ) : null}

          {tab === "strecke" && streckeSubSection ? (
            <OnlineBookingStreckePanel
              canWrite={canWrite}
              subSection={streckeSubSection}
              flow={settings.flow}
              setFlow={settings.setFlow}
              primaryGuestFields={settings.primaryGuestFields}
              setPrimaryGuestFields={settings.setPrimaryGuestFields}
              companionFieldsAdult={settings.companionFieldsAdult}
              setCompanionFieldsAdult={settings.setCompanionFieldsAdult}
              companionFieldsChild={settings.companionFieldsChild}
              setCompanionFieldsChild={settings.setCompanionFieldsChild}
            />
          ) : null}

          {tab === "angebot" && angebotSubSection ? (
            <OnlineBookingAngebotPanel
              canWrite={canWrite}
              subSection={angebotSubSection}
              upselling={settings.upselling}
              setUpselling={settings.setUpselling}
              addons={settings.addons}
              setAddons={settings.setAddons}
              availableArticlesForAddon={settings.availableArticlesForAddon}
              loadUpsellingTemplateSuggestions={settings.loadUpsellingTemplateSuggestions}
              touristIntegrationEnabled={settings.touristIntegrationEnabled}
              arrivalTypes={arrivalTypes}
              arrivalTypesLoading={arrivalTypesLoading}
              onArrivalTypesReload={loadArrivalTypes}
            />
          ) : null}

          {settings.success ? (
            <p className="text-sm text-status-green">Einstellungen gespeichert.</p>
          ) : null}
          {canWrite ? (
            <div className="sticky bottom-4 z-10 flex justify-end rounded-xl border border-separator bg-card/95 p-3 shadow-ios backdrop-blur-md">
              <Button type="submit" loading={settings.saving}>
                Online-Buchung speichern
              </Button>
            </div>
          ) : null}
        </form>
      ) : null}
    </div>
  );
}
